import BigNumber from 'bignumber.js'
import { useMemo } from 'react'
import styled from 'styled-components'
import { Card, CardBody, Flex, Skeleton, Text } from 'vvs-uikit'
import { useTranslation } from 'contexts/Localization'
import LocalBalance from 'components/LocalBalance'
import { useDeserializedFarms } from 'state/farms/useDeserializedFarms'
import { usePriceVvsUsdc } from 'state/farms/hooks'
import { usePools, useVvsVault } from 'state/pools/hooks'
import { BIG_ZERO } from 'utils/bigNumber'
import { getBalanceAmount, getBalanceNumber } from 'utils/formatBalance'
import { convertSharesToVvs } from 'views/Pools/helpers'
import { isBlindMode } from 'utils'
import { useRemovalOrders } from 'state/burn/hooks'
import { getAddress } from 'utils/addressHelpers'
import { useUserVvsBar, useUserVvsBoost } from 'state/xVvs/hooks'
import tokens from 'config/constants/tokens'
import usePriceXvvsUsdc from 'views/xVvs/hooks/usePriceXvvsUsdc'
import { getPoolApr } from 'utils/apr'
import usePastWeekApr from 'views/xVvs/hooks/usePastWeekApr'

const StyledCard = styled(Card)`
  width: 100%;
  height: fit-content;
`

const DepositsCard = () => {
  const { t } = useTranslation()
  const farmsLP = useDeserializedFarms((s) => s.value.farms)

  const vvsPrice = usePriceVvsUsdc()

  const activeFarms = farmsLP.filter((farm) => farm.pid !== 0 && farm.multiplier !== '0X')
  const stakedOnlyFarms = activeFarms.filter(
    (farm) => farm.userData && new BigNumber(farm.userData.stakedBalance).isGreaterThan(0),
  )
  let totalStakedFarms = BIG_ZERO
  stakedOnlyFarms.forEach((farm) => {
    if (farm.lpTotalSupply.gt(0) && farm.lpTotalInQuoteToken.gt(0)) {
      const valueOfBaseTokenInFarm = new BigNumber(farm.tokenPriceUsdc).times(farm.tokenAmountTotal)
      const overallValueOfAllTokensInFarm = valueOfBaseTokenInFarm.times(2)
      const totalLpTokens = getBalanceAmount(farm.lpTotalSupply)
      const lpPrice = overallValueOfAllTokensInFarm.div(totalLpTokens)
      totalStakedFarms = totalStakedFarms.plus(getBalanceNumber(lpPrice.times(farm.userData.stakedBalance)))
    }
  })

  const { pools: poolsWithoutAutoVault } = usePools()
  const rawPools = useMemo(() => {
    const vvsPool = poolsWithoutAutoVault.find((pool) => pool.sousId === 0)
    const vvsAutoVault = { ...vvsPool, isAutoVault: true }
    return [vvsAutoVault, ...poolsWithoutAutoVault]
  }, [poolsWithoutAutoVault])

  const xvvsPriceUsd = usePriceXvvsUsdc()
  const xvvsPastWeekApr = usePastWeekApr()
  const pools = rawPools.map((pool) => {
    if (pool.stakingToken.address !== tokens.xvvs.address) {
      return pool
    }
    // Process pools that uses xVVS as staked tokens
    const minesApr = getPoolApr(
      xvvsPriceUsd.toNumber(),
      pool.earningTokenPrice,
      getBalanceNumber(pool.totalStaked, pool.stakingToken.decimals),
      parseFloat(pool.tokenPerBlock),
    )
    return {
      ...pool,
      stakingTokenPrice: xvvsPriceUsd.toNumber(),
      apr: !pool.isFinished ? minesApr + xvvsPastWeekApr : 0,
      xvvsPastWeekApr,
      minesApr,
    }
  })
  let totalStakedPools = BIG_ZERO
  const {
    userData: { userShares },
    pricePerFullShare,
  } = useVvsVault()
  pools.forEach((pool) => {
    if (pool.isAutoVault) {
      const { vvsAsBigNumber } = convertSharesToVvs(userShares, pricePerFullShare)
      const stakedAutoDollarValue = getBalanceNumber(
        vvsAsBigNumber.multipliedBy(pool.stakingTokenPrice),
        pool.stakingToken.decimals,
      )
      if (!Number.isNaN(stakedAutoDollarValue)) {
        totalStakedPools = totalStakedPools.plus(stakedAutoDollarValue)
      }
    } else {
      const stakedTokenDollarBalance = getBalanceNumber(
        pool.userData.stakedBalance.multipliedBy(pool.stakingTokenPrice),
        pool.stakingToken.decimals,
      )
      if (!Number.isNaN(stakedTokenDollarBalance)) {
        totalStakedPools = totalStakedPools.plus(stakedTokenDollarBalance)
      }
    }
  })

  let totalStakedRemovalOrders = BIG_ZERO
  const orders = useRemovalOrders()
  const userDepositedRemovalOrders = useMemo(
    () => orders.filter((o) => o.userData && o.userData.stakedBalance.isGreaterThan('0')),
    [orders],
  )
  userDepositedRemovalOrders
    .filter((r) => farmsLP.some((f) => getAddress(f.lpAddresses) === r.lpAddress))
    .forEach((r) => {
      const targetFarm = farmsLP.find((f) => getAddress(f.lpAddresses) === r.lpAddress)
      if (r?.userData.stakedBalance.gt(0) && targetFarm.lpTotalSupply.gt(0) && targetFarm.lpTotalInQuoteToken.gt(0)) {
        const valueOfBaseTokenInFarm = new BigNumber(targetFarm.tokenPriceUsdc).times(targetFarm.tokenAmountTotal)
        const overallValueOfAllTokensInFarm = valueOfBaseTokenInFarm.times(2)
        const totalLpTokens = getBalanceAmount(targetFarm.lpTotalSupply)
        const lpPrice = overallValueOfAllTokensInFarm.div(totalLpTokens)
        totalStakedRemovalOrders = totalStakedRemovalOrders.plus(
          getBalanceNumber(lpPrice.times(r.userData.stakedBalance)),
        )
      }
    })

  let totalStakedVvsBoost = BIG_ZERO
  const { barData } = useUserVvsBar()
  const { stakedBalance, totalSupply } = barData
  const rate = stakedBalance.gt(0) && totalSupply.gt(0) ? stakedBalance.div(totalSupply).toNumber() : 1
  const { boostData } = useUserVvsBoost()
  const { stakes } = boostData
  stakes
    .filter((x) => x.active)
    .forEach((x) => {
      totalStakedVvsBoost = totalStakedVvsBoost.plus(
        getBalanceAmount(x.amount, tokens.xvvs.decimals).times(rate).times(vvsPrice),
      )
    })

  const totalDeposit = totalStakedFarms.plus(totalStakedPools).plus(totalStakedRemovalOrders).plus(totalStakedVvsBoost)

  return (
    <StyledCard>
      <CardBody>
        <Flex
          flexDirection={['column', null, null, 'row']}
          justifyContent="space-between"
          alignItems={['flex-start', null, null, 'center']}
        >
          <Flex flexDirection="column" alignItems={['flex-start', null, null, 'flex-start']}>
            {isBlindMode() ? (
              <Skeleton width={96} height={24} my="2px" />
            ) : (
              <Text color="blue" bold fontSize="24px" style={{ lineHeight: 1 }}>
                <LocalBalance
                  decimals={totalDeposit.gt(0) ? 2 : 0}
                  fontSize="24px"
                  bold
                  prefix={totalDeposit.gt(0) ? '~' : ''}
                  lineHeight="1.1"
                  value={totalDeposit}
                />
              </Text>
            )}
            <Text color="textSubtle" fontSize="13px" style={{ whiteSpace: 'nowrap' }}>
              {t('Total Deposits')}
            </Text>
          </Flex>
        </Flex>
      </CardBody>
    </StyledCard>
  )
}

export default DepositsCard
